import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Mail, Plus, Edit, Trash2, Send, Eye, RefreshCw,
  CheckCircle2, XCircle, Clock, AlertCircle, Search,
  Filter
} from 'lucide-react';
import { api } from '@/lib/api';

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  category: string;
  variables: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailLog {
  id: number;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  templateName: string | null;
  status: string;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface EmailStats {
  total: number;
  sent: number;
  failed: number;
  pending: number;
  testMode: number;
  byDay: { date: string; total: number; sent: number; failed: number }[];
  byTemplate: { templateId: number; templateName: string; count: number }[];
}

export default function EmailPage() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('templates');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewData, setPreviewData] = useState<{subject: string; body: string} | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [logFilter, setLogFilter] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [sendForm, setSendForm] = useState({
    to: '',
    recipientName: '',
    variables: {} as Record<string, string>
  });
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'general',
    variables: '',
    isActive: true
  });

  useEffect(() => {
    loadTemplates();
    loadLogs();
    loadStats();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.get('/api/emailtemplates');
      if (res.data.success) setTemplates(res.data.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFilter) params.append('status', logFilter);
      if (logSearch) params.append('search', logSearch);
      
      const res = await api.get(`/api/emaillogs?${params.toString()}`);
      if (res.data.success) setLogs(res.data.data.items);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/api/emaillogs/stats');
      if (res.data.success) setStats(res.data.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const createTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    try {
      const res = await api.post('/api/emailtemplates', templateForm);
      if (res.data.success) {
        alert('تم إنشاء القالب بنجاح');
        setShowTemplateModal(false);
        resetTemplateForm();
        loadTemplates();
      } else {
        alert(res.data.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const updateTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const res = await api.put(`/api/emailtemplates/${editingTemplate.id}`, templateForm);
      if (res.data.success) {
        alert('تم تحديث القالب بنجاح');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        resetTemplateForm();
        loadTemplates();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const deleteTemplate = async (id: number) => {
    if (!confirm('هل أنت متأكد من حذف هذا القالب؟')) return;
    try {
      const res = await api.delete(`/api/emailtemplates/${id}`);
      if (res.data.success) loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const previewTemplate = async (template: EmailTemplate) => {
    try {
      const vars = template.variables?.split(',').reduce((acc, v) => {
        acc[v.trim()] = `[${v.trim()}]`;
        return acc;
      }, {} as Record<string, string>) || {};

      const res = await api.post(`/api/emailtemplates/${template.id}/preview`, vars);
      if (res.data.success) {
        setPreviewData(res.data.data);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Error previewing template:', error);
    }
  };

  const openSendModal = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    const vars = template.variables?.split(',').reduce((acc, v) => {
      acc[v.trim()] = '';
      return acc;
    }, {} as Record<string, string>) || {};
    setSendForm({ to: '', recipientName: '', variables: vars });
    setShowSendModal(true);
  };

  const sendEmail = async () => {
    if (!selectedTemplate || !sendForm.to) {
      alert('يرجى إدخال البريد الإلكتروني');
      return;
    }
    try {
      const res = await api.post(`/api/emailtemplates/${selectedTemplate.id}/send`, sendForm);
      if (res.data.success) {
        alert('تم إرسال البريد بنجاح');
        setShowSendModal(false);
        loadLogs();
        loadStats();
      } else {
        alert(res.data.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      subject: '',
      body: '',
      category: 'general',
      variables: '',
      isActive: true
    });
  };

  const openEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category,
      variables: template.variables || '',
      isActive: template.isActive
    });
    setShowTemplateModal(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent': return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 ml-1" />تم الإرسال</Badge>;
      case 'failed': return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 ml-1" />فشل</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 ml-1" />قيد الإرسال</Badge>;
      case 'test_mode': return <Badge className="bg-blue-100 text-blue-700"><AlertCircle className="w-3 h-3 ml-1" />وضع الاختبار</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'customer': return 'bg-blue-100 text-blue-700';
      case 'shipment': return 'bg-green-100 text-green-700';
      case 'marketing': return 'bg-purple-100 text-purple-700';
      case 'security': return 'bg-red-100 text-red-700';
      case 'support': return 'bg-orange-100 text-orange-700';
      case 'order': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            نظام البريد الإلكتروني
          </h1>
          <p className="text-muted-foreground mt-1">إدارة القوالب وسجل البريد المرسل</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">الإجمالي</p>
                  <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                </div>
                <Mail className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">تم الإرسال</p>
                  <p className="text-2xl font-bold text-green-900">{stats.sent}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700">فشل</p>
                  <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-700">قيد الإرسال</p>
                  <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-700">وضع الاختبار</p>
                  <p className="text-2xl font-bold text-indigo-900">{stats.testMode}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="space-y-4">
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'templates' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            القوالب
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 font-medium transition ${
              activeTab === 'logs' 
                ? 'border-b-2 border-blue-600 text-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            سجل البريد
          </button>
        </div>

        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => { resetTemplateForm(); setEditingTemplate(null); setShowTemplateModal(true); }} 
                      className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Plus className="w-4 h-4" />
                قالب جديد
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="relative">
                  <div className={`absolute top-0 left-0 right-0 h-1 ${
                    template.isActive ? 'bg-gradient-to-r from-blue-500 to-cyan-600' : 'bg-gray-400'
                  }`} />
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{template.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-1">{template.subject}</p>
                      </div>
                      <Badge className={getCategoryColor(template.category)}>{template.category}</Badge>
                    </div>
                    
                    {template.variables && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">المتغيرات:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.variables.split(',').map((v, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{v.trim()}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 pt-3 border-t">
                      <Button size="sm" variant="outline" onClick={() => previewTemplate(template)}>
                        <Eye className="w-3 h-3 ml-1" />معاينة
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openSendModal(template)}>
                        <Send className="w-3 h-3 ml-1" />إرسال
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(template)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteTemplate(template.id)} className="text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="بحث..."
                      value={logSearch}
                      onChange={(e) => setLogSearch(e.target.value)}
                      className="pr-10"
                    />
                  </div>
                  <select
                    value={logFilter}
                    onChange={(e) => setLogFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg bg-white"
                  >
                    <option value="">كل الحالات</option>
                    <option value="sent">تم الإرسال</option>
                    <option value="failed">فشل</option>
                    <option value="pending">قيد الإرسال</option>
                    <option value="test_mode">وضع الاختبار</option>
                  </select>
                  <Button onClick={loadLogs} variant="outline">
                    <Filter className="w-4 h-4 ml-2" />تطبيق
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-12">
                    <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">لا يوجد سجلات</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-right p-3 font-semibold">المستلم</th>
                          <th className="text-right p-3 font-semibold">الموضوع</th>
                          <th className="text-right p-3 font-semibold">القالب</th>
                          <th className="text-right p-3 font-semibold">الحالة</th>
                          <th className="text-right p-3 font-semibold">التاريخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr key={log.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <div>
                                <p className="font-semibold">{log.recipientName || 'غير محدد'}</p>
                                <p className="text-xs text-gray-500">{log.recipientEmail}</p>
                              </div>
                            </td>
                            <td className="p-3 max-w-xs truncate">{log.subject}</td>
                            <td className="p-3">
                              {log.templateName ? (
                                <Badge variant="outline">{log.templateName}</Badge>
                              ) : '-'}
                            </td>
                            <td className="p-3">{getStatusBadge(log.status)}</td>
                            <td className="p-3 text-sm text-gray-500">
                              {new Date(log.createdAt).toLocaleString('ar-EG')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingTemplate ? 'تحديث القالب' : 'إنشاء قالب جديد'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">اسم القالب *</label>
                  <Input
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                    placeholder="welcome"
                    disabled={!!editingTemplate}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الفئة</label>
                  <select
                    value={templateForm.category}
                    onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="general">عام</option>
                    <option value="customer">عملاء</option>
                    <option value="shipment">شحنات</option>
                    <option value="marketing">تسويق</option>
                    <option value="security">أمان</option>
                    <option value="support">دعم</option>
                    <option value="order">طلبات</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">الموضوع *</label>
                <Input
                  value={templateForm.subject}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                  placeholder="موضوع البريد"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">المحتوى (HTML) *</label>
                <textarea
                  value={templateForm.body}
                  onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                  placeholder="<div>...</div>"
                  className="w-full px-3 py-2 border rounded-lg min-h-[300px] font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">المتغيرات (مفصولة بفاصلة)</label>
                <Input
                  value={templateForm.variables}
                  onChange={(e) => setTemplateForm({ ...templateForm, variables: e.target.value })}
                  placeholder="CustomerName,OrderNumber,TotalAmount"
                />
                <p className="text-xs text-gray-500 mt-1">استخدم النمط {"{{VariableName}}"} في المحتوى</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={templateForm.isActive}
                  onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="isActive" className="text-sm font-medium">نشط</label>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={editingTemplate ? updateTemplate : createTemplate} 
                        className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {editingTemplate ? 'تحديث' : 'إنشاء'}
                </Button>
                <Button variant="outline" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>معاينة البريد</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">الموضوع:</p>
                <p className="font-bold">{previewData.subject}</p>
              </div>
              <div className="border rounded-lg p-4 bg-white">
                <div dangerouslySetInnerHTML={{ __html: previewData.body }} />
              </div>
              <Button onClick={() => setShowPreviewModal(false)} className="w-full">إغلاق</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>إرسال بريد من: {selectedTemplate.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">البريد الإلكتروني *</label>
                  <Input
                    type="email"
                    value={sendForm.to}
                    onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })}
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">اسم المستلم</label>
                  <Input
                    value={sendForm.recipientName}
                    onChange={(e) => setSendForm({ ...sendForm, recipientName: e.target.value })}
                    placeholder="احمد محمد"
                  />
                </div>
              </div>
              
              {Object.keys(sendForm.variables).length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-2">المتغيرات</label>
                  <div className="space-y-2">
                    {Object.entries(sendForm.variables).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2">
                        <Badge variant="outline" className="min-w-[120px]">{key}</Badge>
                        <Input
                          value={value}
                          onChange={(e) => setSendForm({
                            ...sendForm,
                            variables: { ...sendForm.variables, [key]: e.target.value }
                          })}
                          placeholder={`قيمة ${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button onClick={sendEmail} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Send className="w-4 h-4 ml-2" />إرسال
                </Button>
                <Button variant="outline" onClick={() => setShowSendModal(false)} className="flex-1">إلغاء</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
